<template>
  <v-dialog
    v-model="showDialog"
    ref="dialog"
    id="short-apply-dialog"
    persistent
    transition="slide-x-transition"
    max-width="800px"
    scrollable
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        color="primary"
        v-bind="attrs"
        v-on="on"
        :block="block || (!inlineButton && $vuetify.breakpoint.smAndDown)"
        :class="{ 'mb-md-4': block, 'mb-2 mr-2': inlineButton }"
        :aria-label="$t('application.applyNow')"
        :disabled="disabled"
        aria-haspopup="false"
        aria-expanded="false"
      >
        {{ $t('application.applyNow') }}
      </v-btn>
    </template>

    <v-card
      :class="`alternate apply-form has--title company_${currentCompanyId}`"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
    >
      <v-card-title class="d-flex pb-2">
        <h2 class="flex-grow-1">{{ $t('application.applyNow') }}</h2>
        <div>
          <v-btn
            small
            fab
            class="secondary"
            @click="close(false)"
            :title="$t('close')"
          >
            <v-icon class="accent--text">mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-card-text v-if="joboffer" class="px-4">
        <template v-if="loading">
          <v-skeleton-loader
            type="article, list-item-three-line, actions"
          ></v-skeleton-loader>
        </template>
        <template v-else>
          <h3 class="apply-form__joboffer-name">
            {{ jobName }}
          </h3>

          <v-alert v-if="showErrorAlert" type="error" dismissible class="mt-4">
            {{ errorAlertMessage }}
          </v-alert>

          <v-alert
            v-if="showRegisterPrompt"
            text
            class="mt-4 mb-4"
            color="blue"
          >
            <p class="mb-4">
              {{
                accountRequired
                  ? $t('register.mandatoryNotice')
                  : $t('register.description')
              }}
            </p>
            <div class="text-center">
              <v-btn class="secondary" @click="openRegister">
                {{ $t('register.button') }}
              </v-btn>

              <v-btn class="primary ml-4" @click="goToLogin">
                {{ $t('confirm.signIn') }}
              </v-btn>
            </div>
          </v-alert>

          <ValidationObserver ref="observer">
            <v-form
              v-model="isValid"
              @submit.prevent
              ref="form"
              autocomplete="on"
            >
              <personal-data
                v-model="application"
                :basics-settings="basics"
                :internal="isInternal"
                :shortForm="true"
              ></personal-data>

              <channel-form
                v-if="channelSettings.channelValidationMode !== 0"
                v-model="application"
                :channel-settings="channelSettings"
                :shortForm="true"
              ></channel-form>

              <documents-form
                v-model="application"
                :documents-settings="documentsSettings"
                :joboffer-id="joboffer.jobofferId"
                @beginParsing="parsing = true"
                @endParsing="parsing = false"
                @file-parsed="handleParsingData"
              ></documents-form>

              <div>
                <v-row>
                  <v-col v-if="acceptTalentpoolInApplicationProcess" cols="12">
                    <v-checkbox
                      v-model="talentpoolAccepted"
                      :label="
                        $t('application.acceptTalentpoolInApplicationProcess')
                      "
                      hide-details="auto"
                    />
                  </v-col>

                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors }"
                      name="privacyPolicyConfirmed"
                      rules="isTrue"
                      :custom-messages="{
                        isTrue: $t('validation.privacyPolicyInvalid')
                      }"
                    >
                      <v-checkbox
                        v-model="policyAccepted"
                        :label="`${$t('acceptPrivacyPolicy')} *`"
                        :error-messages="errors"
                        hide-details="auto"
                      />
                    </ValidationProvider>

                    <div class="text--disabled text-sm mt-2">
                      <div
                        v-if="shortPrivacyPolicy"
                        v-html="localize(this.shortPrivacyPolicy)"
                      />
                      <div v-else v-html="$t('privacyPolicyText')" />
                    </div>
                  </v-col>
                </v-row>
              </div>

              <v-row>
                <v-col cols="12" md="6">
                  <v-btn
                    type="reset"
                    class="secondary"
                    :block="$vuetify.breakpoint.smAndDown"
                    @click.prevent="close(false)"
                  >
                    {{ $t('cancel') }}
                  </v-btn>
                </v-col>
                <v-col cols="12" md="6" class="text-right">
                  <v-btn
                    type="submit"
                    :block="$vuetify.breakpoint.smAndDown"
                    :loading="submitting"
                    :disabled="disabled || accountRequired"
                    @click.prevent="submitForm"
                  >
                    {{ $t('application.apply') }}
                    <v-icon class="ml-2">mdi-send</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </ValidationObserver>
        </template>
      </v-card-text>
    </v-card>

    <cv-parser-dialog v-model="parsing"></cv-parser-dialog>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { eventBus } from '@/event-bus.js';
import { v4 as uuid } from 'uuid';

import TrackingService from '@/services/TrackingService.js';
import { readUtm } from '@/helpers/UtmHelper.js';
import { readReferrerAttribution } from '@/helpers/ReferrerHelper.js';
import basicsFieldsEnums from '@/enum/application-basics-fields.js';

import personalData from './personal-data.vue';
import documentsForm from './documents-short-form.vue';
import cvParserDialog from './cv-parser-dialog.vue';
import channelForm from './channel-form.vue';
import {
  saveApplicationDraft,
  loadApplicationDraft,
  clearApplicationDraft,
  mergeApplicationDraft
} from '@/helpers/ApplicationDraftStorage.js';

export default {
  props: {
    joboffer: {
      type: Object,
      required: false,
      default: () => {}
    },

    inlineButton: {
      type: Boolean,
      required: false,
      default: false
    },

    block: {
      type: Boolean,
      required: false,
      default: false
    },

    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data: () => ({
    showDialog: false,
    loading: true,
    parsing: false,
    application: {
      title: '',
      firstName: '',
      lastName: '',
      email: '',
      confirmEmail: '',
      salutationId: null,
      channel: '',
      channelComment: '',
      isInternal: false,
      referrer: '',
      photo: null,
      cv: [],
      letter: [],
      letterText: '',
      documents: [],
      graphs: [],
      customAttachments: {}
    },
    isValid: false,
    submitting: false,
    basics: [],
    documentsSettings: null,
    questions: null,
    showErrorAlert: false,
    alertErrorMessage: '',
    shortPrivacyPolicy: null,
    policyAccepted: false,
    formStarted: false,
    applicationStartTrackingReady: false,
    applicationCloseTracked: false,
    talentpoolAccepted: false
  }),

  computed: {
    ...mapState('companyModule', {
      slug: 'slug',
      acceptTalentpoolInApplicationProcess:
        'acceptTalentpoolInApplicationProcess',
      customDataPrivacyUrl: 'customDataPrivacyUrl',
      currentCompanyId: 'currentCompanyId',
      candidateAccountMode: 'candidateAccountMode'
    }),

    ...mapState({
      currentLanguage: 'currentLanguage'
    }),

    ...mapGetters('companyModule', {
      isInternal: 'userIsInternal'
    }),

    ...mapGetters('candidateModule', {
      candidateIsAuthenticated: 'isAuthenticated',
      candidateProfile: 'profile'
    }),

    // Whether the signed-in candidate declared themselves internal in their
    // profile. Informational only — never used to gate internal-job access.
    profileIsInternal() {
      return !!(this.candidateProfile && this.candidateProfile.isInternal);
    },

    showRegisterPrompt() {
      return this.candidateAccountMode > 0 && !this.candidateIsAuthenticated;
    },

    accountRequired() {
      return this.candidateAccountMode === 2 && !this.candidateIsAuthenticated;
    },

    applicationStartPayload() {
      return {
        application: this.application,
        basics: this.basics,
        questions: this.questions,
        talentpoolAccepted: this.talentpoolAccepted,
        policyAccepted: this.policyAccepted
      };
    },

    jobName() {
      return this.isInternal
        ? this.localize(this.joboffer.internalName) || this.localize(this.joboffer.name)
        : this.localize(this.joboffer.name);
    }
  },

  watch: {
    async showDialog() {
      if (this.showDialog) {
        await this.initializeData();

        // Pre-fill from the signed-in candidate's profile (empty fields only).
        await this.prefillFromProfile();

        // Restore any draft saved when the user left to register.
        this.restoreApplicationDraft();

        TrackingService.trackApplyCta(this.joboffer.jobofferId, 'short_form');
      }
    },

    applicationStartPayload: {
      handler() {
        this.markApplicationFormStarted();
      },
      deep: true
    }
  },

  async mounted() {
    if (!this.formId || this.formId === '') {
      this.setFormId(uuid());
    }
    this.shortPrivacyPolicy = await this.loadShortPrivacyPolicy({
      slug: this.slug,
      jobofferId: this.joboffer.jobofferId
    });
  },

  methods: {
    ...mapActions('applicationModule', {
      loadForm: 'loadForm',
      submitApplication: 'submitApplication'
    }),

    ...mapActions('companyModule', {
      loadShortPrivacyPolicy: 'loadShortPrivacyPolicy'
    }),

    ...mapActions('candidateModule', {
      loadCandidateProfile: 'loadProfile',
      login: 'login',
      reuseCandidateDocument: 'reuseDocument'
    }),

    ...mapMutations('applicationModule', {
      setFormId: 'setFormId'
    }),

    goToLogin() {
      // Sign in via Keycloak, then return to the resumed application (if any) or
      // the cockpit. History-mode routes are absolute URLs with no fragment, which
      // is exactly what Keycloak needs as a redirect target.
      const path = `/${this.slug}/job/${this.joboffer.jobofferId}?apply=true`;

      this.login(`${window.location.origin}${path}`);
    },

    async prefillFromProfile() {
      if (!this.candidateIsAuthenticated) {
        return;
      }

      await this.loadCandidateProfile();

      const profile = this.candidateProfile;
      if (!profile || !profile.exists) {
        return;
      }

      this.setApplicationValueIfEmpty('title', profile.title);
      this.setApplicationValueIfEmpty('firstName', profile.firstName);
      this.setApplicationValueIfEmpty('lastName', profile.lastName);
      this.setApplicationValueIfEmpty('email', profile.email);
      this.setApplicationValueIfEmpty('confirmEmail', profile.email);

      if (
        (this.application.salutationId === null ||
          this.application.salutationId === '') &&
        profile.salutationId !== null &&
        profile.salutationId !== undefined
      ) {
        this.$set(
          this.application,
          'salutationId',
          profile.salutationId.toString()
        );
      }

      this.prefillBasicsFromProfile(profile);

      await this.prefillDocumentsFromProfile(profile);
    },

    setApplicationValueIfEmpty(field, value) {
      if (value && !this.application[field]) {
        this.$set(this.application, field, value);
      }
    },

    // Sets the salutation only when none has been chosen yet. 0/null salutations
    // from the parser are ignored.
    setSalutationIfEmpty(salutation) {
      if (salutation === null || salutation === undefined) {
        return;
      }

      const current = this.application.salutationId;
      if (current === null || current === undefined || current === '') {
        this.$set(this.application, 'salutationId', salutation.toString());
      }
    },

    // Maps the profile's address and birthdate onto the matching application
    // basics fields (by data definition id), only for fields this form shows and
    // only when still empty.
    prefillBasicsFromProfile(profile) {
      const fields = basicsFieldsEnums.ApplicationBasicsFields;

      this.setBasicsValueIfEmpty(fields.AddressStreet, profile.street);
      this.setBasicsValueIfEmpty(fields.AddressZip, profile.zipCode);
      this.setBasicsValueIfEmpty(fields.AddressCity, profile.city);
      this.setBasicsValueIfEmpty(
        fields.Birthdate,
        this.toGermanDate(profile.birthdate)
      );
    },

    setBasicsValueIfEmpty(dataDefinitionId, value) {
      if (!value) {
        return;
      }

      const item = this.basics.find(
        (x) => x.dataDefinitionId === dataDefinitionId
      );

      if (item && (!item.value || item.value === '')) {
        item.value = value;
      }
    },

    // The profile stores the birthdate as an ISO date; basics date fields expect
    // the dd.MM.yyyy format the date picker emits.
    toGermanDate(isoDate) {
      if (!isoDate) {
        return '';
      }

      const [year, month, day] = isoDate.substring(0, 10).split('-');
      if (!year || !month || !day) {
        return '';
      }

      return `${day}.${month}.${year}`;
    },

    // Attaches the stored documents of the latest application to this form,
    // materializing each into the temporary bucket first. Only fills fields the
    // candidate has not already provided.
    async prefillDocumentsFromProfile(profile) {
      const documents = profile.documents || [];
      if (!documents.length) {
        return;
      }

      const attachments = [];
      for (const document of documents) {
        let fileReference = null;
        try {
          fileReference = await this.reuseCandidateDocument(document.id);
        } catch (e) {
          continue;
        }

        if (!fileReference) {
          continue;
        }

        // 0 = CV, 1 = Letter, 2 = Photo, 3 = Attachment
        if (document.type === 0 && !this.hasFiles(this.application.cv)) {
          this.$set(this.application, 'cv', [fileReference]);
        } else if (
          document.type === 1 &&
          !this.hasFiles(this.application.letter)
        ) {
          this.$set(this.application, 'letter', [fileReference]);
        } else if (document.type === 3) {
          attachments.push(fileReference);
        }
      }

      if (attachments.length && !this.hasFiles(this.application.documents)) {
        this.$set(this.application, 'documents', attachments);
      }
    },

    hasFiles(value) {
      return Array.isArray(value) && value.length > 0;
    },

    close(submitted = false) {
      this.showDialog = false;

      if (submitted === true) {
        TrackingService.trackApplySubmit(this.joboffer.jobofferId);
      } else if (this.formStarted && !this.applicationCloseTracked) {
        this.applicationCloseTracked = true;
        TrackingService.trackApplyCancel(this.joboffer.jobofferId);
      }

      if (window.location.href.search(/apply=true/i) >= 0) {
        this.$router.push({
          name: 'jobDetails',
          params: { jobId: this.joboffer.jobofferId }
        });
      }
    },

    handleDragOver() {
      eventBus.$emit('dragover');
    },

    handleDragLeave() {
      eventBus.$emit('dragleave');
    },

    handleParsingData(data) {
      if (data) {
        // Only fill fields the candidate has not already provided (e.g. prefilled
        // from their profile or typed in) so the CV parser never overwrites them.
        this.setApplicationValueIfEmpty('firstName', data.cvData.firstName);
        this.setApplicationValueIfEmpty('lastName', data.cvData.lastName);
        this.setApplicationValueIfEmpty('email', data.cvData.emailAddress);
        this.setSalutationIfEmpty(data.cvData.salutation);

        for (const item of data.applicationBasics) {
          if (!item.value || item.value === '') {
            continue;
          }

          const basicItem = this.basics.find(
            (x) => x.dataDefinitionId === item.dataDefinitionId
          );

          if (!basicItem) {
            this.basics.push(item);
          } else if (!basicItem.value) {
            basicItem.value = item.value;
          }
        }

        if (data.userPhoto && !this.application.photo) {
          this.$set(this.application, 'photo', data.userPhoto);
        }
      }
    },

    async initializeData() {
      this.formStarted = false;
      this.applicationCloseTracked = false;
      this.applicationStartTrackingReady = false;
      this.loading = true;

      const jobId = this.joboffer.jobofferId;
      const formData = await this.loadForm(jobId);

      this.basics = formData.applicationBasicsSettings;
      if (!this.isInternal && this.basics.length > 0) {
        this.basics = this.basics.filter((x) => !x.internal);
      }

      if (this.isInternal && this.basics.length > 0) {
        this.basics = this.basics.filter((x) => !x.externalOnly);
      }

      this.ensureReactiveFormValues(this.basics);

      this.documentsSettings = {
        cvValidationMode: formData.cvValidationMode,
        documentsValidationMode: formData.documentsValidationMode,
        letterValidationMode: formData.letterValidationMode,
        photoValidationMode: formData.photoValidationMode,
        customAttachmentTypes: formData.customAttachmentTypes,
        uploadInfoText: formData.uploadInfoText
      };

      for (const customType of this.documentsSettings.customAttachmentTypes) {
        this.$set(this.application.customAttachments, customType.type, []);
      }

      this.channelSettings = {
        channelValidationMode: formData.channelValidationMode,
        channelCommentValidationMode: formData.channelCommentValidationMode,
        channels: formData.channels
      };

      if (
        formData.applicationQuestions &&
        formData.applicationQuestions.length > 0
      ) {
        this.questions = formData.applicationQuestions;
        this.ensureReactiveFormValues(this.questions);
      }

      // A candidate who declared themselves internal in their profile submits as
      // internal even on the public portal (informational only; it does not grant
      // internal-job access).
      this.$set(
        this.application,
        'isInternal',
        this.isInternal || this.profileIsInternal
      );
      this.loading = false;
      this.$nextTick(() => {
        this.applicationStartTrackingReady = true;
      });
    },

    open() {
      this.showDialog = true;
    },

    ensureReactiveFormValues(items) {
      if (!items) {
        return;
      }

      for (const item of items) {
        if (!Object.prototype.hasOwnProperty.call(item, 'value')) {
          this.$set(item, 'value', null);
        }
      }
    },

    getApplicationReferrer() {
      return this.application.referrer || readReferrerAttribution();
    },

    async submitForm() {
      this.showErrorAlert = false;
      this.errorAlertMessage = '';

      const result = await this.$refs.observer.validate();
      if (result) {
        this.submitting = true;
        this.application.jobofferId = this.joboffer.jobofferId;

        try {
          this.application.acceptTalentpoolInApplicationProcess =
            this.talentpoolAccepted;
          this.application.acceptPrivacyPolicy = this.policyAccepted;
          this.application.basics = this.basics;
          this.application.questions = this.questions;
          this.application.slug = this.slug;
          this.application.disableCvParsing = false;
          this.application.selectedLanguage = this.currentLanguage;
          this.application.referrer = this.getApplicationReferrer();
          this.application.utm = readUtm();

          const response = await this.submitApplication(this.application);
          const result = await response.json();

          if (response.ok && result.success) {
            eventBus.$emit(
              'successMessage',
              this.$t('application.applicationSubmitted')
            );

            clearApplicationDraft(this.joboffer.jobofferId);
            this.close(true);
          } else {
            eventBus.$emit(
              'errorMessage',
              this.$t(`application.errors.${result.errorCode}`)
            );

            this.showErrorAlert = true;
            this.errorAlertMessage = this.$t(
              `application.errors.${result.errorCode}`
            );

            document.querySelector('.v-dialog').scrollTop = 0;
          }
        } catch (error) {
          eventBus.$emit(
            'errorMessage',
            'Es ist ein Fehler beim Speichern der Bewerbung aufgetreten'
          );
        }

        this.submitting = false;
      }
    },

    markApplicationFormStarted() {
      if (
        !this.showDialog ||
        !this.applicationStartTrackingReady ||
        this.loading ||
        this.submitting ||
        this.formStarted
      ) {
        return;
      }

      this.trackApplyStart();
    },

    async trackApplyStart() {
      if (!this.formStarted) {
        this.formStarted = true;

        await TrackingService.trackApplyStart(this.joboffer.jobofferId);
      }
    },

    openRegister() {
      saveApplicationDraft(this.joboffer.jobofferId, this.application);
      this.showDialog = false;
      eventBus.$emit('openRegister');
    },

    restoreApplicationDraft() {
      const draft = loadApplicationDraft(this.joboffer.jobofferId);
      // Only non-empty draft values are copied, so an empty draft field never
      // overwrites data already prefilled from the candidate's profile.
      this.application = mergeApplicationDraft(this.application, draft);
    }
  },

  components: {
    personalData,
    documentsForm,
    cvParserDialog,
    channelForm
  }
};
</script>

<style scoped>
.apply-dialog__parsing-layer {
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding-top: 200px;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 999;
}

h2 {
  color: revert;
  font-weight: 700 !important;
  margin-bottom: 0 !important;
}

.v-card__title h2 {
  font-size: var(--font-size-lg) !important;
}
</style>
